import {
  type Coords,
  type DoneCallback,
  TileLayer,
  type TileLayerOptions,
} from "leaflet";
import {
  createElementObject,
  createTileLayerComponent,
  updateGridLayer,
  withPane,
} from "@react-leaflet/core";
import { type TileLayerProps } from "react-leaflet";
import * as fns from "date-fns";
import {
  type AuthorizeKeyResponse,
  getAuthorizeKeyAPI,
} from "@/features/map/api/getCdsMapAuthorizeKey";
import { type AxiosInstance } from "axios";
import { Mutex } from "async-mutex";
import { useApiClient } from "@/hooks/useApiClient";
import React from "react";

enum AuthState {
  "UNAUTHORIZED",
  "AUTHENTICATED",
}

/**
 * TileLayerWithHeadersの内部にあるoptionsを拡張して認証キーを保存できるようにする
 */
interface CdsTileLayerOptions extends TileLayerOptions {
  authorizeKey: string;
  enableAccessTime: Date;
}

/**
 * CDS地図のタイルレイヤー取得処理＋認証キー取得
 * 下記URLのコードを参考に実装
 * https://github.com/jaq316/leaflet-header
 */
class TileLayerWithHeaders extends TileLayer {
  private authState: AuthState;
  private readonly mutex: Mutex;
  private readonly apiClient: AxiosInstance;
  declare options: CdsTileLayerOptions;

  constructor(
    url: string,
    apiClient: AxiosInstance,
    options?: TileLayerOptions
  ) {
    super(url, options);
    this.authState = AuthState.UNAUTHORIZED;
    this.mutex = new Mutex();
    this.apiClient = apiClient;
  }

  /**
   * 認証キー(AuthorizeKey)を取得する
   */
  public async getAuthorizeKey(): Promise<boolean> {
    // リトライ用カウンタ
    let retryCount = 0;
    // 最大リトライ数
    const MAX_RETRY_COUNT = 1;
    // authorizeKey受信データ
    let authorizeKeyResponse: AuthorizeKeyResponse | undefined;

    do {
      try {
        authorizeKeyResponse = await getAuthorizeKeyAPI(this.apiClient);
      } catch (error) {
        if (retryCount >= MAX_RETRY_COUNT) {
          // リトライ最大値に到達した場合は諦める

          return false;
        }
        // リトライ
        retryCount++;
      }
      if (authorizeKeyResponse) {
        // 受信成功
        break;
      }
    } while (retryCount <= MAX_RETRY_COUNT);

    if (authorizeKeyResponse) {
      this.authState = AuthState.AUTHENTICATED;

      this.options.authorizeKey = authorizeKeyResponse.authorizeKey;

      this.options.enableAccessTime = new Date(
        authorizeKeyResponse.enableaccesstime
      );
      return true;
    }
    return false;
  }

  /**
   * デバッグ用
   * authKeyをコンソールに表示する
   */
  public printAuthKey(): void {
    console.log("this.authorizeKey:", this.options.authorizeKey);
    console.log("this.enableAccessTime:", this.options.enableAccessTime);
    console.log("this.authState:", AuthState[this.authState]);
  }

  /**
   * 認証キーの期限が切れていないか確認する
   * @param enableAccessTime
   * @private
   */
  private checkAuthorizeKeyExpired(enableAccessTime: Date): boolean {
    const now = new Date();
    return fns.isAfter(now, enableAccessTime);
  }

  /**
   * 認証キーの有効/無効を判定する
   * @private
   */
  public isAuthorizeKeyValid(): boolean {
    if (!this.options.authorizeKey) {
      return false;
    }

    if (!this.options.enableAccessTime) {
      return false;
    }

    if (this.checkAuthorizeKeyExpired(this.options.enableAccessTime)) {
      this.authState = AuthState.UNAUTHORIZED;
      return false;
    }
    return true;
  }

  /**
   * 意図的にAuthorizeKeyAccessTimeを破壊する
   */
  public breakAuthorizeKeyAccessTime(): void {
    console.log("breakAuthorizeKeyAccessTime");
    this.options.enableAccessTime = new Date();
    this.authState = AuthState.UNAUTHORIZED;
  }

  /**
   * 意図的にAuthorizeKeyを破壊する
   */
  public breakAuthorizeKey(): void {
    console.log("breakAuthorizeKey");
    this.options.authorizeKey = "abcde";
    this.authState = AuthState.UNAUTHORIZED;
  }

  /**
   * 認証キーが認証済みか確認し、期限が切れているなら再取得を行う
   * @return boolean true:成功、false:失敗
   */
  public getAuthorizeKeyWithMutex = async (): Promise<boolean> => {
    // 認証キーが認証済みか確認
    // mutexを使用して同時実行を防止する
    const release = await this.mutex.acquire();
    if (!this.isAuthorizeKeyValid()) {
      // キーなし or 無効
      if (await this.getAuthorizeKey()) {
        // 取得成功
        release();
        return true;
      } else {
        // キー再取得失敗
        release();
        return false;
      }
    } else {
      // キー有効
      release();
      return true;
    }
  };

  /**
   * タイルデータ取得関数
   * タイルデータを表示するタイミングでleafletから呼び出される
   * 1タイル毎に呼び出され、1タイル分のデータをdone()コールバックに渡して呼び出せばOK
   * @param coords
   * @param done
   * @protected
   */
  protected createTile(coords: Coords, done: DoneCallback): HTMLImageElement {
    const img = document.createElement("img");

    (async (): Promise<void> => {
      // 認証キーが認証済みか確認
      await this.getAuthorizeKeyWithMutex();

      img.setAttribute("role", "presentation");

      // リトライ用カウンタ
      let retryCount = 0;
      // 最大リトライ数
      const MAX_RETRY_COUNT = 1;
      // タイル受信データ
      let tileResponse: Response | undefined;

      // 認証エラーの場合は500が返ってくるが、実際は500のエラーの前にCORSのエラーが起こる
      // これはサーバーが返すレスポンスヘッダーに
      // 「access-control-allow-origin: *」が含まれないことが原因
      //
      // 応答が200の場合は「access-control-allow-origin: *」が付与されているが
      // 応答が500の場合これが付与されていない
      // そのため、500が返ってきているかどうかでエラーを検知する処理がうまく働かない
      //
      // 今回は例外が発生した場合はリトライする処理で実装する
      do {
        try {
          const url = this.getTileUrl(coords);
          tileResponse = await fetch(url, { method: "GET" });
        } catch (_) {
          if (retryCount >= MAX_RETRY_COUNT) {
            // リトライ最大値に到達した場合は諦める
            done(undefined, img);
            return;
          }
          // 認証キーを再取得してリトライ
          await this.getAuthorizeKey();
          retryCount++;
        }
        if (tileResponse?.ok) {
          // 受信成功
          break;
        } else {
          // 例外にはならなかったが200系の応答ではなかった場合、リトライ
          retryCount++;
        }
      } while (retryCount <= MAX_RETRY_COUNT);

      if (tileResponse?.ok) {
        // タイルデータ受信成功
        const blob = await tileResponse.blob();
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === "string") {
            img.src = reader.result;
          }
        };
        reader.readAsDataURL(blob);
        done(undefined, img);
      } else {
        // 何度かリトライしたがtileResponseがokにならない場合は諦める
        done(undefined, img);
      }
    })();
    return img;
  }
}

/**
 * CdsTileLayerコンポーネントが、apiClientをpropsとして受け取ることができるようにするインターフェース
 */
interface CdsTileLayerProps extends TileLayerProps {
  apiClient: AxiosInstance;
  setDisableOpenStreetMap: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * CDS地図を返すコンポーネント
 */
const CdsTileLayer = createTileLayerComponent(
  function createTileLayer({ url, ...options }: CdsTileLayerProps, context) {
    const { apiClient, setDisableOpenStreetMap } = options;
    const layer = new TileLayerWithHeaders(
      url,
      apiClient,
      withPane(options, context)
    );

    // デバッグ用, n秒毎に意図的に認証キーを破壊する
    // (() => {
    //   setInterval(() => {
    //     layer.breakAuthorizeKey();
    //     layer.breakAuthorizeKeyAccessTime();
    //   }, 2 * 1000);
    // })();

    layer.on("add", () => {
      // CDS地図レイヤーが有効の時はOpenStreetMapの読み込みを無効化する
      setDisableOpenStreetMap(true);

      // 認証キーを取得
      (async () => {
        await layer.getAuthorizeKeyWithMutex();
      })();
    });

    layer.on("remove", () => {
      setDisableOpenStreetMap(false);
    });

    return createElementObject(layer, context);
  },
  function updateTileLayer(layer, props, prevProps) {
    updateGridLayer(layer, props, prevProps);
    const { url } = props;
    if (url != null && url !== prevProps.url) {
      layer.setUrl(url);
    }
  }
);

/**
 * CdsTileLayerWithHookのpropsを定義
 * setDisableOpenStreetMapがtrueになっている間、標準の地図の読み込みが一時停止する
 */
interface CdsTileLayerWithHookProps extends TileLayerProps {
  setDisableOpenStreetMap: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CdsTileLayerWithHook = (
  props: CdsTileLayerWithHookProps & React.RefAttributes<TileLayerWithHeaders>
): React.ReactElement => {
  const { apiClient } = useApiClient();

  return <CdsTileLayer apiClient={apiClient} {...props} />;
};
